import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, useQueries, useQueryClient } from '@tanstack/react-query';
import { MdOutlineInsertChart } from 'react-icons/md';
import {
  CardSize,
  ChartConfig,
  ChartId,
  DashboardId,
  DimensionId
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { AgentSessionManager, AgentSessionManagerHandle } from '@Components/AgentChat';
import { Drawer } from '@Components/Drawer';
import { useAppSelector } from '@src/redux';
import { roleSelector } from '@src/redux/user';
import { AgentResponse } from '../../utils/agent-client';
import { Agent, AgentType, useAgentEndpoint } from '../../utils/agent-endpoints';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { readStorage, writeStorage } from '../../utils/hooks/useData';
import {
  useDashboardAgentDrawer,
  usePendingAgentPrompt,
  useTemplateTip
} from '../../redux/dashboardAgent';
import {
  CardType,
  ChartCardConfig,
  DashboardCard,
  DashboardConfig
} from '../Dashboard/dashboard-types';
import { useSetError } from '../../redux/modals';
import {
  appendChartToDashboard,
  createDashboardFromCharts,
  SizedChart,
  upsertDashboardInList,
  useCanEditDashboard,
  useDashboard
} from '../Dashboard/hooks/useDashboard';
import { DASHBOARD_AGENT_TIPS } from './dashboard-agent-constants';
import {
  ChartWithData,
  chartDataKeyPrefix,
  chartConfigQueryOptions,
  useChartConfig,
  useChartEnvelope
} from '../Charts/hooks/useChartData';
import { useRecordPermissions } from '../Charts/hooks/useRecordPermissions';
import {
  seedWorkingCopy,
  useWorkingCopy,
  workingCopyQueryOptions
} from '../Charts/hooks/useWorkingCopy';
import { seedPendingPromotion, usePendingPromotion } from '../Charts/hooks/usePendingPromotion';
import {
  seedPendingSharing,
  sharingTargetKey,
  usePendingSharing
} from '../Charts/hooks/usePendingSharing';
import { assignableRoleLevels, SharingRequestTarget } from '../Charts/chart-permissions';
import { resolveRequestedLevels } from '../Charts/sharing-request-utils';
import { ChartHandle, isDraftId, newDraftId, previewChartIdFor } from '../Charts/chart-handle';
import { saveChart } from '../Charts/chart-save';
import {
  extractAllEmittedConfigs,
  extractEmittedDashboardConfig,
  extractLayoutChanges,
  extractPromotion,
  extractSharingRequest,
  cardsInSlotOrder,
  pickUnseenTip,
  sizedChartForPlan
} from './dashboard-agent-utils';
import { usePendingLayoutChanges } from '../Dashboard/hooks/usePendingLayoutChanges';
import { useBufferedLayout } from '../Dashboard/hooks/useBufferedLayout';
import { useDashboardViewSnapshot } from '../Dashboard/hooks/useDashboardViewSnapshot';
import {
  buildFocusContext,
  FocusedArtifact,
  FocusedChartSummary
} from './dashboard-agent-grounding';
import { useSavedChartSummaries } from './useDashboardingGrounding';
import { useAgentDispatcher } from './useAgentDispatcher';

// The single-dashboard route is /:company/dashboard/<dashboardId> (a splat after the key). Pull the
// id out so we can tell when the drawer is open on a real, saved dashboard.
// Tips already shown this browser session. Per-tab and best-effort: storage can be unavailable
// (private mode, blocked site data), in which case tips simply aren't remembered.
const SEEN_TIPS_KEY = 'dashboardAgentSeenTips';
const readSeenTips = (): string[] => {
  try {
    return readStorage<string[]>(sessionStorage, SEEN_TIPS_KEY) ?? [];
  } catch {
    return [];
  }
};
const writeSeenTips = (seen: string[]): void => {
  try {
    writeStorage(sessionStorage, SEEN_TIPS_KEY, seen);
  } catch {
    // Not remembered; the worst case is a repeated tip.
  }
};

const dashboardIdFromPath = (pathname: string): DashboardId | null => {
  const match = pathname.match(/\/dashboard\/([^/?#]+)/);
  return match ? match[1] : null;
};

// The focused chart from /:company/charts/<handle>: a saved `chart:<uuid>` or an unsaved `draft:<uuid>`
// the agent is still editing.
const handleFromPath = (pathname: string): ChartHandle | null => {
  const match = pathname.match(/\/charts\/((?:chart|draft):[^/?#]+)/);
  return match ? (match[1] as ChartHandle) : null;
};

// The drawer is restricted to the dashboard and charts pages (their index views and any
// sub-route — a specific dashboard at /:company/dashboard/<id> or chart at /:company/charts/<id>).
// It must not appear anywhere else in the app yet.
const isDashboardOrChartsRoute = (pathname: string): boolean =>
  Boolean(
    matchPath({ path: '/:company/dashboard/*', end: false }, pathname) ||
    matchPath({ path: '/:company/charts/*', end: false }, pathname)
  );

// The chart ids placed on a list of cards, in the list's order.
const chartIdsFromCards = (cards: DashboardCard[] = []): ChartId[] =>
  cards
    .filter(c => c.type === CardType.CHART && c.config)
    .map(c => (c.config as ChartCardConfig).chartId);

// The chart ids currently placed on a saved dashboard, read fresh from the dashboard query cache.
// Used in the imperative response handler (where a non-reactive snapshot is what's wanted).
const dashboardChartIds = (
  queryClient: QueryClient,
  company: string,
  dashboardId: DashboardId
): ChartId[] =>
  chartIdsFromCards(
    queryClient.getQueryData<DashboardConfig>(['dashboard', company, dashboardId])?.cards
  );

// A saved chart's compiled config from the chart-data cache (populated once the chart has rendered);
// undefined until then. The prefix-key match ignores the per-view date/kpi override suffix.
const cachedChartConfig = (
  queryClient: QueryClient,
  company: string,
  chartId: ChartId
): ChartConfig | undefined => {
  const entries = queryClient.getQueriesData<ChartWithData>({
    queryKey: chartDataKeyPrefix(company, chartId)
  });
  return entries.find(([, data]) => data?.config)?.[1]?.config;
};

const chartSummary = (
  handle: ChartHandle,
  config: ChartConfig | undefined
): FocusedChartSummary => ({
  id: handle,
  title: config?.title,
  type: config?.type
});

// Name a dashboard's chart to the agent. Prefer the saved-chart metadata (reliable title/type,
// available without waiting for the card to render); fall back to the render cache, then to id-only.
const summarizeDashboardChart = (
  chartId: ChartId,
  savedSummaries: Map<ChartId, FocusedChartSummary>,
  queryClient: QueryClient,
  company: string
): FocusedChartSummary =>
  savedSummaries.get(chartId) ??
  chartSummary(chartId, cachedChartConfig(queryClient, company, chartId));

// The dashboard agent as an omnipresent panel, available on every company page. It's an INLINE
// flex sibling of the page content (not an overlay), so opening it pushes the page rather than
// covering it. AgentSessionManager stays mounted while collapsed, so the chat session survives
// closing/reopening the drawer, but `startFresh` gives each landing on a dashboard/charts page a
// new chat rather than resuming a stale one — past chats stay reachable via History. Ask for a
// chart → it's saved and you're taken to it; ask for a dashboard → a whole dashboard is built;
// ask to add a chart while viewing a dashboard → it's appended to that dashboard in place.
export const AgentDrawer = () => {
  const { pathname } = useLocation();
  const { cid: company } = useCompanyInfo();
  // Checked before mounting the drawer body so its queries don't run on every page.
  if (!company || !isDashboardOrChartsRoute(pathname)) return null;
  return <DashboardAgentDrawer />;
};

const DashboardAgentDrawer = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const agentEndpoint = useAgentEndpoint(Agent.DASHBOARD_CHARTS);
  const { cid: company } = useCompanyInfo();

  // What the user is currently viewing — a saved dashboard and/or a standalone chart (from the
  // route). Drives both the per-turn focus context and the append-vs-edit routing below.
  const dashboardId = dashboardIdFromPath(pathname);
  const focusedHandle = handleFromPath(pathname);

  // Shared rather than local so a control elsewhere in the app can open the panel (the dashboards
  // list's "Build with agent" card, the template tip). `Drawer` is controlled from here as a result.
  const { drawerOpen, setDrawerOpen } = useDashboardAgentDrawer();
  const { templateTip } = useTemplateTip();
  const { pendingPrompt, setPendingPrompt } = usePendingAgentPrompt();
  const sessionRef = useRef<AgentSessionManagerHandle>(null);

  // A template landing gets a fresh chat (an in-progress one stays in History). Keyed by dashboard
  // so a second template picked in the same page load gets its own — and so StrictMode's dev-only
  // double-invoke doesn't start two.
  const landedOn = templateTip?.dashboardId ?? null;
  const newChatForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!landedOn || newChatForRef.current === landedOn) return;
    newChatForRef.current = landedOn;
    sessionRef.current?.startNewSession();
  }, [landedOn]);

  // A chat just seeded by the template tip already got its tip, so it gets no in-chat tip on top of
  // that. Cleared when the next chat starts.
  const [tipSuppressed, setTipSuppressed] = useState(false);

  // Text handed over from outside the drawer, consumed once. Only the template tip sends it, so it
  // also marks this chat as tipped; an empty string means that tip had no starter prompt to offer.
  useEffect(() => {
    if (pendingPrompt === null) return;
    if (pendingPrompt) {
      sessionRef.current?.prefillPrompt(pendingPrompt);
    }
    setPendingPrompt(null);
    setTipSuppressed(true);
  }, [pendingPrompt, setPendingPrompt]);

  // One prompt tip at a time, never repeated within the browser session. A tip only counts as seen
  // once it's actually drawn, so picking one for a chat that already has messages doesn't use it up.
  const [promptTip, setPromptTip] = useState(() =>
    pickUnseenTip(DASHBOARD_AGENT_TIPS, readSeenTips())
  );
  const markTipSeen = useCallback((tip: string) => {
    const seen = readSeenTips();
    if (!seen.includes(tip)) {
      writeSeenTips([...seen, tip]);
    }
  }, []);
  // A fresh moment to offer a new tip: the drawer opening, or a new chat starting.
  const refreshTip = useCallback(() => {
    setPromptTip(prev => {
      const seen = readSeenTips();
      return prev && !seen.includes(prev) ? prev : pickUnseenTip(DASHBOARD_AGENT_TIPS, seen);
    });
  }, []);
  useEffect(() => {
    if (drawerOpen) {
      refreshTip();
    }
  }, [drawerOpen, refreshTip]);
  const handleNewSession = useCallback(() => {
    setTipSuppressed(false);
    refreshTip();
  }, [refreshTip]);
  // Only a saved chart has a row to fetch; a draft renders purely from its working copy.
  const focusedChartId = isDraftId(focusedHandle) ? null : focusedHandle;

  // The focused single chart's CURRENT saved config, loaded reactively (not from the render cache,
  // which is empty until the chart has drawn). This is what lets the agent edit an already-loaded
  // chart faithfully instead of rebuilding it from scratch. Skipped for a draft (no saved row).
  const { data: focusedConfig } = useChartConfig(company, focusedChartId);

  // The focused chart's ENVELOPE — the row's own levels — for the sharing grounding. Same query the
  // chart page uses, so this costs no extra request. `useChartConfig` above is a projection of it and
  // carries no levels.
  const { data: focusedEnvelope } = useChartEnvelope(company, focusedChartId);
  const userRole = useAppSelector(roleSelector);

  // Whether THIS USER may edit the focused chart, for the "YOUR ACCESS" grounding line — same gate
  // the chart page itself uses. `isResolved` is false until the envelope arrives, and that must NOT
  // read as "view only": an unresolved gate would have the agent refuse a chart the user can
  // perfectly well edit, so `undefined` (say nothing) is passed through instead of a premature
  // `false`.
  const { isResolved: chartPermissionsResolved, canEdit: chartCanEdit } =
    useRecordPermissions(focusedEnvelope);

  // The focused chart's unsaved working copy (the agent's proposed, not-yet-saved edits) plus a writer
  // for it. The agent must be grounded on what the user is LOOKING AT — the working copy when one
  // exists — so a follow-up edit stacks onto the current preview, not the stale saved row. Keyed by the
  // handle so it also covers a brand-new draft in focus.
  const { workingCopy, setWorkingCopy } = useWorkingCopy(company, focusedHandle);

  // A chat-driven promotion ("save it" / "make a new one" / "discard") is written here as a one-shot
  // note; the single-chart view consumes it and runs its exit handlers (so the fan-out confirm gates
  // a chat Update the same as the button). Keyed by the focused handle.
  const { setPendingPromotion } = usePendingPromotion(company, focusedHandle);

  // Where a chat-driven sharing request is left for the page that owns the record. Two targets,
  // because the agent names which one it meant.
  const { setPendingSharing: setChartSharing } = usePendingSharing(company, focusedHandle);
  const { setPendingSharing: setDashboardSharing } = usePendingSharing(
    company,
    dashboardId ? sharingTargetKey(dashboardId) : null
  );

  // The focused dashboard's config, loaded REACTIVELY (subscribes to the same query the dashboard
  // page uses, so no extra fetch). This is what was missing: reading it via a one-time
  // getQueryData snapshot meant the focus context computed once — before the dashboard had loaded
  // (just navigated / just created) — and never recomputed when its data arrived, so the agent saw
  // an "unnamed dashboard with no charts". Saved-chart summaries name those charts to the agent.
  const { data: focusedDashboard } = useDashboard(company, dashboardId ?? undefined);
  // The drawer mounts outside the dashboard page, so it works the permission out from the same
  // query the page uses rather than receiving it as a prop — through the same hook the page calls,
  // so the two cannot disagree about who may change a dashboard.
  const setError = useSetError();
  const canEditDashboard = useCanEditDashboard(focusedDashboard);
  // Where a chat-driven dashboard change is handed to the dashboard, which holds it pending until
  // Save/Discard.
  const { setPendingLayoutChanges } = usePendingLayoutChanges(company, dashboardId ?? null);
  const savedChartSummaries = useSavedChartSummaries(company);

  // The focused dashboard's cards AS SHOWN, in the order their move_card slots are numbered. The
  // builder's buffer when it has published one, so pending changes (a move, a remove, a resize) are
  // what the agent sees — else the saved layout. NOT sorted by grid position: the slot number has to
  // mean what `moveCardToSlot` counts, which is array order. See `cardsInSlotOrder`.
  const bufferedCards = useBufferedLayout(company, dashboardId ?? null);
  const viewSnapshot = useDashboardViewSnapshot(company, dashboardId ?? null);
  const focusedDashboardCards = useMemo(
    () =>
      dashboardId && focusedDashboard
        ? cardsInSlotOrder(bufferedCards ?? focusedDashboard.cards)
        : [],
    [dashboardId, focusedDashboard, bufferedCards]
  );

  // The chart ids currently on the focused dashboard, in slot order. (Named to avoid the
  // module-level `dashboardChartIds` cache-snapshot helper used by the response handler below.)
  const focusedDashboardChartIds = useMemo(
    () => chartIdsFromCards(focusedDashboardCards),
    [focusedDashboardCards]
  );

  // The charts a handoff can carry: the focused single chart, else the focused dashboard's.
  const focusedChartIds = useMemo<ChartHandle[]>(
    () => (focusedHandle ? [focusedHandle] : focusedDashboardChartIds),
    [focusedHandle, focusedDashboardChartIds]
  );
  const dispatcher = useAgentDispatcher({ company, focusedChartIds });

  // Each chart's CARD size on this dashboard. Off the cards rather than off the chart, because size
  // is a placement fact: the same chart can be standard here and wide on another dashboard.
  const cardSizeByChartId = useMemo(() => {
    const byChart = new Map<ChartId, CardSize>();
    focusedDashboardCards.forEach(card => {
      if (card.config?.chartId) {
        byChart.set(card.config.chartId, card.size);
      }
    });
    return byChart;
  }, [focusedDashboardCards]);

  const ignoreByChartId = useMemo(() => {
    const byChart = new Map<ChartId, DimensionId[]>();
    focusedDashboardCards.forEach(card => {
      if (card.config?.chartId && card.config.ignoreDashboardFilters) {
        byChart.set(card.config.chartId, card.config.ignoreDashboardFilters);
      }
    });
    return byChart;
  }, [focusedDashboardCards]);

  // Each dashboard chart's SAVED config, loaded REACTIVELY so the agent can edit it FAITHFULLY — the
  // same reason the single focused chart carries its config. Without it a SURGICAL edit ("remove the
  // filter", "add revenue") is impossible: the agent can't see what to change and either refuses or
  // rebuilds from scratch. Same query key as each card's own useChartConfig, so these dedupe (no extra
  // network). useQueries keeps the focus context current as configs arrive (a plain getQueryData
  // snapshot would miss late-arriving configs — the exact staleness the reactive dashboard read fixed).
  const dashboardConfigResults = useQueries({
    queries: focusedDashboardChartIds.map(id => chartConfigQueryOptions(company, id))
  });
  // A card's pending preview (working copy) takes precedence over its saved config, so a follow-up edit
  // on a dashboard stacks on the current preview instead of the stale saved row (parity with the single
  // chart). Read reactively via the SAME unified working-copy store (keyed by chartId) the card writes —
  // via the shared query-options factory so these observers can't race the card's own read.
  const dashboardWorkingCopyResults = useQueries({
    queries: focusedDashboardChartIds.map(id => workingCopyQueryOptions(queryClient, company, id))
  });
  const dashboardChartConfigById = useMemo(() => {
    const map = new Map<ChartId, ChartConfig>();
    focusedDashboardChartIds.forEach((id, i) => {
      const config = dashboardWorkingCopyResults[i]?.data ?? dashboardConfigResults[i]?.data;
      if (config) {
        map.set(id, config);
      }
    });
    return map;
  }, [focusedDashboardChartIds, dashboardConfigResults, dashboardWorkingCopyResults]);

  // Per-turn focus context: names the artifact in focus so the agent can edit it in place by
  // re-emitting with its id. Both the focused single chart AND each dashboard chart carry their FULL
  // config so edits are faithful. Recomputed when the route, the focused chart's config, or the focused
  // dashboard's contents/configs change (so a freshly loaded dashboard's charts and configs land).
  const focusContext = useMemo<string | undefined>(() => {
    const focus: FocusedArtifact = {};
    if (focusedHandle) {
      // Prefer the working copy — the version on screen — so refinements build on the current preview.
      // For a draft this IS the only config (no saved row), so the agent grounds on the draft itself.
      const groundedConfig = workingCopy ?? focusedConfig;
      focus.chart = { ...chartSummary(focusedHandle, groundedConfig), config: groundedConfig };
      // The row's own audiences, narrowed to the two level fields the formatter reads — never the
      // whole envelope (createdBy, config, etc. don't belong in the grounding struct).
      if (focusedEnvelope) {
        focus.chart.sharing = {
          viewRoleRequired: focusedEnvelope.viewRoleRequired,
          editRoleRequired: focusedEnvelope.editRoleRequired
        };
      }
      // Whether THIS USER may edit it. `undefined` (not `false`) while the gate hasn't resolved yet —
      // see the hook call above for why a premature "view only" is worse than silence.
      focus.chart.canEdit = chartPermissionsResolved ? chartCanEdit : undefined;
    }
    if (dashboardId && focusedDashboard) {
      focus.dashboard = {
        id: dashboardId,
        name: focusedDashboard.name,
        // The dashboard's OWN audiences. Never derived from its charts' — a dashboard and its charts
        // are governed separately, and one internal chart must not narrow the whole dashboard.
        sharing:
          focusedDashboard.viewRoleRequired === undefined ||
          focusedDashboard.editRoleRequired === undefined
            ? undefined
            : {
                viewRoleRequired: focusedDashboard.viewRoleRequired,
                editRoleRequired: focusedDashboard.editRoleRequired
              },
        charts: focusedDashboardChartIds.map((id, i) => ({
          ...summarizeDashboardChart(id, savedChartSummaries, queryClient, company),
          // Its place in the dashboard's card order, which is what move_card takes.
          slot: i + 1,
          config: dashboardChartConfigById.get(id),
          // The card's CURRENT size, so a resize request is grounded rather than guessed — and so a
          // size the frontend's gap-filling corrected is visible on the next turn.
          size: cardSizeByChartId.get(id),
          ignoreDashboardFilters: ignoreByChartId.get(id)
        })),
        date: viewSnapshot?.dateRange,
        defaultFilters: focusedDashboard.defaultFilters,
        currentFilters: viewSnapshot?.dashboardFilters,
        // Whether THIS USER may edit the dashboard itself — the same gate the append-new-charts path
        // below already checks, via the same `useCanEditDashboard` the dashboard page uses.
        canEdit: canEditDashboard
      };
    }
    // What this user may assign, so the agent refuses an impossible audience in prose rather than
    // silently handing the frontend a request it will only reject. Only when something is in focus:
    // with nothing to share there is nothing to say.
    if (focus.chart || focus.dashboard) {
      focus.assignableLevels = assignableRoleLevels(userRole);
    }
    return buildFocusContext(focus);
  }, [
    focusedHandle,
    focusedConfig,
    focusedEnvelope,
    chartPermissionsResolved,
    chartCanEdit,
    workingCopy,
    dashboardId,
    focusedDashboard,
    canEditDashboard,
    focusedDashboardChartIds,
    dashboardChartConfigById,
    cardSizeByChartId,
    ignoreByChartId,
    viewSnapshot,
    savedChartSummaries,
    company,
    queryClient,
    userRole
  ]);

  const handleAgentResponse = useCallback(
    async (response: AgentResponse, respondingAgent?: AgentType) => {
      // Only the dashboard agent's output carries chart and layout changes.
      if (respondingAgent && respondingAgent !== Agent.DASHBOARD_CHARTS) return;
      try {
        // 0. Dashboard changes (card layout, card filter opt-outs, dashboard filters). Handed to
        //    DashboardBuilder as pending changes rather than written here, so a card change runs
        //    the same buffer-and-reflow the card's own controls do. Read up front because it is
        //    independent of the branches below — a turn can move a card without touching any chart
        //    config — but handed off only once any append this turn has saved and refetched (see
        //    `handOffLayoutChanges`), or the re-seed that append triggers would land on top of it.
        //
        //    This is the enforcement point for dashboard changes. Grounding tells the agent whether
        //    the user may edit the dashboard, but it can go stale mid-session; refusing here is
        //    what makes editor-only true rather than aspirational.
        const extractedLayoutChanges = extractLayoutChanges(response);
        if (extractedLayoutChanges.length > 0 && !canEditDashboard) {
          setError({
            message: "You don't have edit access to this dashboard, so it was left unchanged."
          });
        }
        const layoutChanges = canEditDashboard ? extractedLayoutChanges : [];
        const handOffLayoutChanges = () => {
          if (layoutChanges.length > 0) {
            setPendingLayoutChanges(layoutChanges);
          }
        };

        // All four extractions are pure reads of the response. Hoisted together so the routing below
        // can ask whether this turn's emit actually touches the focused chart, or mints a whole new
        // dashboard — the ORDER THAT MATTERS is the order notes are SEEDED, not the order the
        // response is parsed.
        const sharing = extractSharingRequest(response);
        const configs = extractAllEmittedConfigs(response);
        const promotion = extractPromotion(response);
        const plan = extractEmittedDashboardConfig(response);

        // Does an emit in this turn refine the chart in focus, or is the agent making a separate new
        // one? The file's existing config routing already asks this; the sharing routing must ask it
        // too, or a request for a NEW chart gets written onto whatever happened to be open.
        const editsFocused = isDraftId(focusedHandle)
          ? configs[0]
          : configs.find(c => c.id === focusedChartId);

        // A turn that emits a chart which is NOT an edit of the focused one will mint a brand-new
        // draft below, and a chart-targeted sharing request belongs to THAT draft.
        const willMintDraft =
          !dashboardId && configs.length > 0 && !(focusedHandle && editsFocused);

        // Does this turn CREATE a chart? Only a config with NO id mints a new row; one carrying an
        // id upserts a chart that already exists, and re-sharing an existing record from a dashboard
        // is not something chat can do — that is the card case, which the agent is told to decline.
        const willCreateChart = configs.some(config => !config.id);

        // A chat-driven sharing request, ROUTED here to whichever record it is about — and left as a
        // note for the page that owns that record. Three of the arms below are deliberately empty:
        // the record does not exist yet, so the note is seeded further down, once the plan or the
        // draft that will own it has an id.
        //
        // A note only; nothing is written here. The page runs the same permission check, ceiling and
        // offered lists the sharing dialog runs — see useSharingRequest. What makes "make me a chart
        // of X, share it with everyone, and save it" land with the audience attached is not this
        // block's position but the draft's own first save, which resolves the requested levels and
        // sends them with the row it creates.
        if (sharing) {
          if (sharing.target === SharingRequestTarget.DASHBOARD) {
            if (plan) {
              // The dashboard this request is about does not exist yet. Seeded onto the new
              // dashboard's id after the plan creates it, below — never onto whatever dashboard the
              // user happens to be looking at, which is a record they did not mention.
            } else if (dashboardId) {
              setDashboardSharing(sharing);
            } else {
              setError({
                message: "Open a dashboard first, then ask again to change who it's shared with."
              });
            }
          } else if (willMintDraft) {
            // Belongs to the draft minted below, NOT to the chart that happens to be open. Seeded
            // there, once, on that draft's handle.
          } else if (dashboardId && willCreateChart) {
            // A new chart being appended to this dashboard: the levels ride along with the save that
            // creates it, in the append loop below.
          } else if (dashboardId) {
            // An edit to a chart already on this dashboard, or a bare request with nothing new. A
            // card's sharing is changed on the chart's own page — say so rather than dropping it.
            setError({
              message:
                'A chart on a dashboard is shared from its own page. Open the chart, then ask ' +
                "again to change who it's shared with."
            });
          } else if (focusedHandle) {
            setChartSharing(sharing);
          } else {
            setError({
              message:
                "Open a chart or a dashboard first, then ask again to change who it's shared with."
            });
          }
        }

        // 1. Whole-dashboard plan: save each chart (preserving order), auto-lay-out, create the
        //    dashboard, then open it.
        if (plan) {
          const charts: SizedChart[] = [];
          // Sequential to preserve the agent's chart order, which drives the layout.
          for (const { config, size, ignoreDashboardFilters } of plan.charts) {
            // `size` is a HINT the agent may omit or get wrong, so it is run through the
            // chart-type size matrix rather than trusted -- see `sizedChartForPlan`.
            charts.push(
              sizedChartForPlan(
                (await saveChart(company, config)).id,
                config.type,
                size,
                ignoreDashboardFilters
              )
            );
          }
          const dashboard = await createDashboardFromCharts(
            company,
            plan.name,
            charts,
            plan.defaultFilters
          );
          // The plan's own sharing request, now that the dashboard it is about has an id.
          if (sharing?.target === SharingRequestTarget.DASHBOARD) {
            seedPendingSharing(queryClient, company, sharingTargetKey(dashboard.id), sharing);
          }
          upsertDashboardInList(queryClient, company, dashboard);
          // Refresh the saved-chart metadata so the just-built charts are named (not bare ids) in
          // the focus context once the new dashboard opens.
          await queryClient.invalidateQueries({ queryKey: ['dashboarding-bootstrap', company] });
          navigate(`/${company}/dashboard/${dashboard.id}`);
          return;
        }

        // Off a dashboard (the standalone chart view or the library): the agent PROPOSES — nothing
        // persists. An emit becomes an unsaved working copy that renders as a preview; a promotion is
        // a one-shot note the single-chart view consumes to run its exits (Update / Save as a new
        // chart / Discard). Route by whether an emit EDITS the focused artifact or is a genuinely NEW
        // chart:
        //  - focused DRAFT: any emit refines that draft's single working copy.
        //  - focused SAVED chart, emit re-uses its id: a faithful in-place refinement.
        //  - anything else (a NEW id while a chart is focused — the agent chose to make a separate
        //    chart — or nothing focused, i.e. the library): a brand-new chart. Mint a client-side
        //    draft so it's its own unsaved thing instead of clobbering the focused chart's working
        //    copy; it opens with Save chart / Discard this draft. Nothing hits the DB.
        if (!dashboardId) {
          if (configs.length > 0) {
            if (focusedHandle && editsFocused) {
              // Refining the focused artifact: a saved chart keeps its own id; a draft borrows its
              // handle's (the emit itself carries none — the database assigns ids, not the agent).
              setWorkingCopy({
                ...editsFocused,
                id: isDraftId(focusedHandle)
                  ? previewChartIdFor(focusedHandle)
                  : (focusedChartId as ChartId)
              });
              // "make it a bar chart and save it": apply the edit, then promote it in place.
              if (promotion) {
                setPendingPromotion(promotion);
              }
            } else {
              const draftId = newDraftId();
              seedWorkingCopy(queryClient, company, draftId, {
                ...configs[0],
                id: previewChartIdFor(draftId)
              });
              // "make a new chart of X and save it": carry the keep onto the draft it lands on.
              if (promotion) {
                seedPendingPromotion(queryClient, company, draftId, promotion);
              }
              // "Make me a line chart of X, visible to everyone with access to this client": the
              // audience rides onto the draft this lands on, and its first save applies it.
              if (sharing && sharing.target === SharingRequestTarget.CHART) {
                seedPendingSharing(queryClient, company, draftId, sharing);
              }
              navigate(`/${company}/charts/${draftId}`);
            }
          } else if (promotion && focusedHandle) {
            // A bare "save it" / "discard" with no new edit this turn — promote the current preview.
            setPendingPromotion(promotion);
          }
          return;
        }

        // On a dashboard the agent PROPOSES too — it no longer silently auto-saves. An emit that EDITS
        // a chart already on the dashboard lands as an unsaved WORKING COPY on that card, previewed in
        // place; the user promotes it via the card's own exits (Update everywhere it's used / Save as a
        // new chart / Discard). An emit with a NEW id is a genuinely new chart: save it and append it
        // (that flow is unchanged). Chat promotion verbs are consumed on the card, not here.
        if (configs.length === 0) {
          handOffLayoutChanges();
          return;
        }

        // A dashboard is in focus (off-dashboard returned above). Ids already on it are edits (seed a
        // preview); ids not present are new charts to append. Snapshot the member ids once, up front.
        const existingIds = dashboardChartIds(queryClient, company, dashboardId);
        const newCharts: SizedChart[] = [];

        // A chart created on this dashboard is a FIRST SAVE, exactly like a draft's own, so the
        // requested audience goes through the same resolver `SingleChartView` uses: the assignability
        // rule AND the ceiling, in one place, so the two surfaces cannot promise different things.
        // `levels: null` means the request named an audience this caller may not assign; the backend
        // would have clamped it silently, so this path must not promise it either.
        //
        // Resolved once, outside the loop: the same request applies to every new chart this turn.
        const requestedSharing =
          sharing?.target === SharingRequestTarget.CHART
            ? resolveRequestedLevels(sharing, userRole)
            : null;

        // Sequential to keep a deterministic order (new charts append in emit order).
        for (const config of configs) {
          if (config.id && existingIds.includes(config.id)) {
            // Edit-in-place → preview: seed this chart's working copy (unified store, keyed by chartId)
            // instead of saving the shared chart. Focusing the chart later shows this same pending edit.
            seedWorkingCopy(queryClient, company, config.id, config as ChartConfig);
            continue;
          }
          // Levels ride along ONLY for a config with no id — that is the one that mints a row, so it
          // is a first save. A config carrying an id upserts a chart that already exists, and
          // re-sharing an existing record is not this path's business.
          //
          // BOTH levels go on the wire, not just the half the request named: the ceiling cannot be
          // applied to one without the other, so `resolveRequestedLevels` fills the unnamed half with
          // the create default the backend itself would have used. Same pair, computed on the side
          // that can tell the user about it.
          // The chart's TYPE is carried alongside its id, not dropped: the append below writes the
          // placement, and its size has to come from the chart-type size matrix (a `stat` allows
          // only `one-third`) rather than a blanket default. See `sizedChartForPlan`.
          newCharts.push(
            sizedChartForPlan(
              (
                await saveChart(
                  company,
                  config,
                  !config.id ? (requestedSharing?.levels ?? undefined) : undefined
                )
              ).id,
              config.type,
              undefined
            )
          );
        }

        // 2. On a dashboard: append any NEW charts and refresh their metadata so their titles/types are
        //    named in the focus context. Edits above touched no saved row, so nothing else to refresh.
        //    Stay put. (Off a dashboard the code returned above, so a dashboard is always in focus here.)
        if (newCharts.length > 0) {
          await queryClient.invalidateQueries({ queryKey: ['dashboarding-bootstrap', company] });

          // Both conditions can fire on the SAME turn (a new chart with an unassignable audience, on
          // a dashboard this user cannot edit) — the shared error modal renders only the LAST
          // `setError` call, so two separate calls here silently dropped the sharing notice whenever
          // both were true. Collected into one call instead of gating either off, so both pieces of
          // information reach the user.
          const notices: string[] = [];
          if (requestedSharing?.levels === null) {
            notices.push(
              "The requested audience for the new chart isn't one you can assign, so it was " +
                'created with the default audience instead.'
            );
          }
          // The ceiling moved an audience the user actually ASKED for. Said out loud because this
          // surface has nowhere to show the resolved pair — the chart page's draft footer renders it,
          // so it needs no sentence, but here the alternative is a chart quietly created on narrower
          // terms than the agent's own reply just described.
          if (requestedSharing?.editNarrowed) {
            notices.push(
              'Nobody can edit a chart they cannot see, so the new chart was made editable by the ' +
                'same audience that can view it.'
            );
          }
          // Appending a card WRITES the dashboard, so it needs the dashboard's own edit right —
          // not the chart's. The charts above are already saved either way, so say where they went
          // rather than dropping them silently.
          if (!canEditDashboard) {
            notices.push(
              "You don't have edit access to this dashboard, so the new charts were saved to " +
                'your chart library instead.'
            );
          }
          if (notices.length > 0) {
            setError({ message: notices.join(' ') });
          }

          if (canEditDashboard) {
            for (const chart of newCharts) {
              await appendChartToDashboard(company, dashboardId, chart);
            }
            await queryClient.invalidateQueries({ queryKey: ['dashboard', company, dashboardId] });
          }
        }

        // 3. Layout changes, now that any append above has landed.
        handOffLayoutChanges();
      } catch (e) {
        console.error('[AgentDrawer] handling agent response failed', e);
        setError({
          message: `The agent's changes couldn't be applied: ${e.message}`,
          reportError: e
        });
      }
    },
    [
      company,
      dashboardId,
      focusedHandle,
      focusedChartId,
      navigate,
      queryClient,
      setPendingLayoutChanges,
      setWorkingCopy,
      setPendingPromotion,
      setChartSharing,
      setDashboardSharing,
      // Without this, the permission check above reads whatever the dashboard's edit right was when
      // this callback was last built — a stale `true` on a dashboard the user may not change.
      canEditDashboard,
      // Read by the append path's assignability gate — without it, a role change mid-session would
      // leave this callback checking a stale role against a freshly requested audience.
      userRole,
      setError
    ]
  );

  if (!company) return null;

  return (
    <Drawer
      title='Dashboard Agent'
      toggleIcon={<MdOutlineInsertChart />}
      toggleAriaLabel='Ask the dashboard agent'
      open={drawerOpen}
      onOpenChange={setDrawerOpen}
    >
      <AgentSessionManager
        ref={sessionRef}
        agentEndpoint={agentEndpoint}
        agent={Agent.DASHBOARD_CHARTS}
        company={company}
        focusContext={focusContext}
        onAgentResponse={handleAgentResponse}
        placeholder='Build or edit a dashboard...'
        // Only one tip at a time: none while the header's template tip is pending, nor in the chat
        // that tip just seeded.
        promptTip={templateTip || tipSuppressed ? undefined : promptTip}
        onPromptTipShown={markTipSeen}
        onNewSession={handleNewSession}
        startFresh
        {...dispatcher}
      />
    </Drawer>
  );
};
